import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import MonthlyReportButton from '@/components/MonthlyReportButton';
import { 
  MapPin, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  ChevronRight,
  ClipboardList,
  Shield,
  Leaf,
  FileSearch,
  Building,
  TreePine,
  Droplets,
  Mountain,
  X
} from 'lucide-react';

// Phase icon mapping based on common phase names
const getPhaseIcon = (phaseName) => {
  const name = phaseName?.toLowerCase() || '';
  
  if (name.includes('diagnóstico') || name.includes('diagnostico') || name.includes('levantamento')) {
    return FileSearch;
  }
  if (name.includes('campo') || name.includes('vistoria') || name.includes('visita')) {
    return MapPin;
  }
  if (name.includes('relatório') || name.includes('relatorio') || name.includes('laudo') || name.includes('documento')) {
    return FileText;
  }
  if (name.includes('licença') || name.includes('licenciamento') || name.includes('autorização')) {
    return Shield;
  }
  if (name.includes('ambiental') || name.includes('meio ambiente')) {
    return Leaf;
  }
  if (name.includes('construção') || name.includes('obra') || name.includes('instalação')) {
    return Building;
  }
  if (name.includes('florestal') || name.includes('vegetação') || name.includes('mata')) {
    return TreePine;
  }
  if (name.includes('água') || name.includes('hídrico') || name.includes('efluente')) {
    return Droplets;
  }
  if (name.includes('mineração') || name.includes('solo') || name.includes('terra')) {
    return Mountain;
  }
  
  return ClipboardList;
};

// Get status configuration
const getStatusConfig = (status, completion) => {
  if (completion >= 100) {
    return {
      label: 'Concluído',
      color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      iconColor: 'text-emerald-500',
      bgColor: 'bg-emerald-50'
    };
  }
  if (status === 'in_progress' || completion > 0) {
    return {
      label: 'Em andamento',
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50'
    };
  }
  if (status === 'delayed' || status === 'overdue') {
    return {
      label: 'Atrasado',
      color: 'bg-red-100 text-red-700 border-red-200',
      iconColor: 'text-red-500',
      bgColor: 'bg-red-50'
    };
  }
  return {
    label: 'Não iniciado',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    iconColor: 'text-gray-400',
    bgColor: 'bg-gray-50'
  };
};

// Get task status configuration
const getTaskStatusConfig = (status) => {
  switch (status) {
    case 'completed':
      return {
        label: 'Concluída',
        color: 'bg-emerald-100 text-emerald-700',
        icon: CheckCircle2,
        iconColor: 'text-emerald-500'
      };
    case 'in_progress':
      return {
        label: 'Em andamento',
        color: 'bg-blue-100 text-blue-700',
        icon: Clock,
        iconColor: 'text-blue-500'
      };
    case 'cancelled':
      return {
        label: 'Cancelada',
        color: 'bg-gray-100 text-gray-500',
        icon: X,
        iconColor: 'text-gray-400'
      };
    default:
      return {
        label: 'Pendente',
        color: 'bg-amber-100 text-amber-700',
        icon: AlertCircle,
        iconColor: 'text-amber-500'
      };
  }
};

// Project Card Component
const ProjectCard = ({ project, phases, onPhaseClick }) => {
  const statusConfig = getStatusConfig(project.status, project.completion_percentage);
  const activePhases = useMemo(() => phases.filter(p => p.active !== false), [phases]);
  const currentPhase = phases.find(p => p.completion_percentage < 100 && p.active !== false) || phases[phases.length - 1];
  
  return (
    <Card className="overflow-hidden transition-all duration-300 hover:shadow-lg border border-gray-100 group">
      {/* Top color accent */}
      <div className={`h-2 ${project.completion_percentage >= 100 ? 'bg-emerald-500' : 'bg-emerald-600'}`} />
      
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 pr-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-emerald-700 transition-colors">
              {project.name}
            </h3>
            {project.location && (
              <div className="flex items-center text-sm text-gray-500">
                <MapPin className="w-4 h-4 mr-1" />
                {project.location}
              </div>
            )}
          </div>
          <Badge className={`${statusConfig.color} border shrink-0`}>
            {statusConfig.label}
          </Badge>
        </div>
        
        {/* Progress Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progresso Geral</span>
            <span className="text-sm font-bold text-emerald-700">
              {project.completion_percentage?.toFixed(0) || 0}%
            </span>
          </div>
          <Progress 
            value={project.completion_percentage || 0} 
            className="h-3 bg-gray-100"
          />
        </div>
        
        {/* Current Phase Highlight */}
        {currentPhase && (
          <div className="bg-emerald-50 rounded-lg p-4 mb-6 border border-emerald-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                {React.createElement(getPhaseIcon(currentPhase.name), { 
                  className: "w-5 h-5 text-emerald-600" 
                })}
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium uppercase tracking-wide">Fase Atual</p>
                <p className="text-sm font-semibold text-gray-900">{currentPhase.name}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Phases Timeline */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Etapas do Projeto</p>
          <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-thin">
            {activePhases.map((phase, index) => {
              const phaseStatus = getStatusConfig(
                phase.completion_percentage >= 100 ? 'completed' : 
                phase.completion_percentage > 0 ? 'in_progress' : 'not_started',
                phase.completion_percentage
              );
              const PhaseIcon = getPhaseIcon(phase.name);
              const isLast = index === activePhases.length - 1;
              
              return (
                <React.Fragment key={phase.phase_id}>
                  <button
                    onClick={() => onPhaseClick(project, phase, phases)}
                    className={`flex flex-col items-center p-2 rounded-lg transition-all duration-200 
                      hover:bg-gray-50 hover:scale-105 cursor-pointer min-w-[70px]
                      ${phase.completion_percentage >= 100 ? 'opacity-100' : 
                        phase.completion_percentage > 0 ? 'opacity-100' : 'opacity-60'}`}
                    title={phase.name}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1.5
                      ${phase.completion_percentage >= 100 ? 'bg-emerald-100 ring-2 ring-emerald-500' :
                        phase.completion_percentage > 0 ? 'bg-blue-100 ring-2 ring-blue-500' :
                        'bg-gray-100'}`}
                    >
                      <PhaseIcon className={`w-5 h-5 ${phaseStatus.iconColor}`} />
                    </div>
                    <span className="text-xs text-gray-600 text-center line-clamp-2 leading-tight">
                      {phase.name.length > 12 ? phase.name.substring(0, 12) + '...' : phase.name}
                    </span>
                  </button>
                  
                  {!isLast && (
                    <div className={`w-6 h-0.5 ${
                      phases[index + 1]?.completion_percentage > 0 || phase.completion_percentage >= 100
                        ? 'bg-emerald-400' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        
        {/* View Details Button */}
        <Button 
          variant="outline" 
          className="w-full mt-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 group/btn"
          onClick={() => onPhaseClick(project, currentPhase, phases)}
        >
          Ver Detalhes do Projeto
          <ChevronRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
        </Button>
      </div>
    </Card>
  );
};

// Phase Detail Modal
const PhaseDetailModal = ({ isOpen, onClose, project, selectedPhase, phases, tasks }) => {
  const [activePhase, setActivePhase] = useState(selectedPhase);
  const activePhases = useMemo(() => (phases || []).filter(p => p.active !== false), [phases]);

  useEffect(() => {
    setActivePhase(selectedPhase);
  }, [selectedPhase]);
  
  if (!project || !activePhase) return null;
  
  const phaseTasks = tasks.filter(t => t.phase_id === activePhase.phase_id);
  const statusConfig = getStatusConfig(
    activePhase.completion_percentage >= 100 ? 'completed' : 
    activePhase.completion_percentage > 0 ? 'in_progress' : 'not_started',
    activePhase.completion_percentage
  );
  const PhaseIcon = getPhaseIcon(activePhase.name);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${statusConfig.bgColor} flex items-center justify-center`}>
              <PhaseIcon className={`w-6 h-6 ${statusConfig.iconColor}`} />
            </div>
            <div>
              <p className="text-sm text-gray-500">{project.name}</p>
              <DialogTitle className="text-xl">{activePhase.name}</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {/* Phase Navigation */}
          <div className="py-4 border-b bg-gray-50 -mx-6 px-6">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Navegue pelas Etapas</p>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {activePhases.map((phase, index) => {
                const isActive = phase.phase_id === activePhase.phase_id;
                const phaseStatusConfig = getStatusConfig(
                  phase.completion_percentage >= 100 ? 'completed' : 
                  phase.completion_percentage > 0 ? 'in_progress' : 'not_started',
                  phase.completion_percentage
                );
                
                return (
                  <button
                    key={phase.phase_id}
                    onClick={() => setActivePhase(phase)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap
                      ${isActive 
                        ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' 
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${
                      phase.completion_percentage >= 100 ? 'bg-emerald-500' :
                      phase.completion_percentage > 0 ? 'bg-blue-500' : 'bg-gray-300'
                    }`} />
                    <span className="text-sm font-medium">{phase.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Phase Progress */}
          <div className="py-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge className={`${statusConfig.color} border`}>
                  {statusConfig.label}
                </Badge>
              </div>
              <span className="text-lg font-bold text-emerald-700">
                {activePhase.completion_percentage?.toFixed(0) || 0}% completo
              </span>
            </div>
            <Progress 
              value={activePhase.completion_percentage || 0} 
              className="h-4 bg-gray-100"
            />
          </div>
          
          {/* Tasks Section */}
          <div className="pb-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Atividades desta Etapa
              <Badge variant="outline" className="ml-2">
                {phaseTasks.length} {phaseTasks.length === 1 ? 'atividade' : 'atividades'}
              </Badge>
            </h4>
            
            {phaseTasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Nenhuma atividade cadastrada para esta etapa</p>
              </div>
            ) : (
              <div className="space-y-3">
                {phaseTasks.map((task) => {
                  const taskStatus = getTaskStatusConfig(task.status);
                  const TaskIcon = taskStatus.icon;
                  
                  return (
                    <div 
                      key={task.task_id}
                      className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0
                        ${task.status === 'completed' ? 'bg-emerald-100' :
                          task.status === 'in_progress' ? 'bg-blue-100' : 'bg-gray-100'}`}
                      >
                        <TaskIcon className={`w-5 h-5 ${taskStatus.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{task.name}</p>
                        {task.deadline && (
                          <p className="text-sm text-gray-500">
                            Prazo: {new Date(task.deadline).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                      </div>
                      <Badge className={`${taskStatus.color} shrink-0`}>
                        {taskStatus.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Client Dashboard Component
function ClientDashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectPhases, setProjectPhases] = useState({});
  const [projectTasks, setProjectTasks] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [phaseModalOpen, setPhaseModalOpen] = useState(false);

  useEffect(() => {
    // Redirect non-client users to analytics
    if (user?.role?.toUpperCase() !== 'CLIENT') {
      navigate('/analytics');
      return;
    }
    fetchClientProjects();
  }, [user, navigate]);

  const fetchClientProjects = async () => {
    try {
      setLoading(true);
      
      // Fetch projects (API filters by client automatically based on user)
      const projectsResponse = await api.get('/projects');
      const clientProjects = projectsResponse.data;
      setProjects(clientProjects);
      
      // Fetch phases and tasks for each project
      const phasesMap = {};
      const tasksMap = {};
      
      for (const project of clientProjects) {
        try {
          const [phasesRes, tasksRes] = await Promise.all([
            api.get(`/projects/${project.project_id}/phases`),
            api.get(`/projects/${project.project_id}/tasks`)
          ]);
          phasesMap[project.project_id] = phasesRes.data;
          tasksMap[project.project_id] = tasksRes.data;
        } catch (err) {
          console.error(`Error fetching data for project ${project.project_id}:`, err);
          phasesMap[project.project_id] = [];
          tasksMap[project.project_id] = [];
        }
      }
      
      setProjectPhases(phasesMap);
      setProjectTasks(tasksMap);
    } catch (error) {
      console.error('Error fetching client projects:', error);
      toast.error('Erro ao carregar seus projetos');
    } finally {
      setLoading(false);
    }
  };

  const handlePhaseClick = (project, phase, phases) => {
    setSelectedProject(project);
    setSelectedPhase(phase);
    setPhaseModalOpen(true);
  };

  const closePhaseModal = () => {
    setPhaseModalOpen(false);
    setSelectedProject(null);
    setSelectedPhase(null);
  };

  // Calculate summary stats
  const stats = {
    total: projects.length,
    inProgress: projects.filter(p => p.completion_percentage > 0 && p.completion_percentage < 100).length,
    completed: projects.filter(p => p.completion_percentage >= 100).length,
    notStarted: projects.filter(p => !p.completion_percentage || p.completion_percentage === 0).length
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700 mx-auto mb-4"></div>
            <p className="text-gray-500">Carregando seus projetos...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-2xl p-8 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                Olá, {user?.full_name?.split(' ')[0] || 'Cliente'}! 👋
              </h1>
              <p className="text-emerald-100 text-lg max-w-xl">
                Acompanhe o progresso dos seus projetos ambientais de forma simples e visual.
              </p>
            </div>
            <div className="hidden md:block">
              <Leaf className="w-24 h-24 text-emerald-500/30" />
            </div>
          </div>

          {/* Monthly report download */}
          {user?.user_id && (
            <div className="mt-6 flex flex-wrap items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex-1 min-w-[200px]">
                <p className="text-white font-semibold">Relatório mensal</p>
                <p className="text-emerald-100 text-sm">Baixe o resumo em PDF de seus projetos no mês escolhido.</p>
              </div>
              <MonthlyReportButton
                clientId={user.user_id}
                clientName={user.full_name || 'cliente'}
              />
            </div>
          )}
          
          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-emerald-200 text-sm">Total de Projetos</p>
              <p className="text-3xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-emerald-200 text-sm">Em Andamento</p>
              <p className="text-3xl font-bold">{stats.inProgress}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-emerald-200 text-sm">Concluídos</p>
              <p className="text-3xl font-bold">{stats.completed}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-emerald-200 text-sm">Não Iniciados</p>
              <p className="text-3xl font-bold">{stats.notStarted}</p>
            </div>
          </div>
        </div>

        {/* Trust Message */}
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Shield className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-800 mb-1">Acompanhamento Contínuo</h3>
            <p className="text-emerald-700">
              Estamos acompanhando todas as etapas do seu projeto para garantir o cumprimento 
              dos prazos e exigências ambientais. Sua tranquilidade é nossa prioridade.
            </p>
          </div>
        </div>

        {/* Projects Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Seus Projetos</h2>
            <Badge variant="outline" className="text-gray-500">
              {projects.length} {projects.length === 1 ? 'projeto' : 'projetos'}
            </Badge>
          </div>

          {projects.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhum projeto encontrado
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Você ainda não possui projetos cadastrados. Entre em contato conosco para 
                iniciar seu projeto ambiental.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <ProjectCard
                  key={project.project_id}
                  project={project}
                  phases={projectPhases[project.project_id] || []}
                  onPhaseClick={handlePhaseClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Phase Detail Modal */}
        <PhaseDetailModal
          isOpen={phaseModalOpen}
          onClose={closePhaseModal}
          project={selectedProject}
          selectedPhase={selectedPhase}
          phases={selectedProject ? (projectPhases[selectedProject.project_id] || []) : []}
          tasks={selectedProject ? (projectTasks[selectedProject.project_id] || []) : []}
        />
      </div>
    </Layout>
  );
}

export default ClientDashboard;
